import { AiFillDelete } from "react-icons/ai";
import { MdLockReset } from "react-icons/md";
import { Breadcrumb } from "react-bootstrap";
import "./qrCodeCreation.css";
import Accordion from "react-bootstrap/Accordion";
import { useState, useRef, useEffect, ChangeEvent } from "react";
import QRCodeStyling, {
  DrawType,
  TypeNumber,
  Mode,
  ErrorCorrectionLevel,
  DotType,
  CornerSquareType,
  CornerDotType,
  Options,
} from "qr-code-styling";
import { UserAuth } from "../../context/AuthContext";
import Form from "react-bootstrap/Form";

//Dot Types
import DotType_Square from "../Assets/dotTypes/Square-2.png";
import DotType_Classy from "../Assets/dotTypes/Classy-2.png";
import DotType_Rounded from "../Assets/dotTypes/Rounded-2.png";

//Corner Squares
import CornerSquare_Square from "../Assets/cornerSquares/CornerSquareSquare-2.png";
import CornerSquare_Dot from "../Assets/cornerSquares/CornerSquareDot-2.png";
import CornerSquare_ExtraRounded from "../Assets/cornerSquares/CornerSquareExtraRounded-2.png";

// Corner Dots
import CornerDot_Square from "../Assets/cornerDots/CornerDotSquare-2.png";
import CornerDot_Dot from "../Assets/cornerDots/CornerDotDot-2.png";

import QRCodeModal from "./QRCodeModal";
import { useNavigate } from "react-router-dom";

const MAX_TITLE_LENGTH = 25;
const MAX_DESCRIPTION_LENGTH = 200;
const MAX_PASSWORD_LENGTH = 50;

const sanitizeInput = (input: string): string => {
  return input
    .trim()
    .replace(/[<>]/g, "")
    .replace(/javascript:/gi, "")
    .replace(/on\w+=/gi, "");
};

const isValidInput = (input: string): boolean => {
  // Check for potentially dangerous patterns
  const dangerousPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, // Script tags
    /javascript:/gi, // JavaScript protocol
    /data:/gi, // Data protocol
    /vbscript:/gi, // VBScript protocol
    /on\w+=/gi, // Inline event handlers
  ];

  return !dangerousPatterns.some((pattern) => pattern.test(input));
};

const QRCodeCreation = () => {
  const { user, roles } = UserAuth();
  const { premium, business } = roles;
  const [options, setOptions] = useState<Options>({
    width: 300,
    height: 300,
    type: "svg" as DrawType,
    data: "http:/google.co.uk",
    image: "",
    margin: 10,
    qrOptions: {
      typeNumber: 0 as TypeNumber,
      mode: "Byte" as Mode,
      errorCorrectionLevel: "Q" as ErrorCorrectionLevel,
    },
    imageOptions: {
      hideBackgroundDots: true,
      imageSize: 0.5,
      margin: 20,
      crossOrigin: "anonymous",
    },
    dotsOptions: {
      color: "#222222",
      type: "rounded" as DotType,
    },
    backgroundOptions: {
      color: "#5FD4F3",
    },
    cornersSquareOptions: {
      color: "#222222",
      type: "extra-rounded" as CornerSquareType,
    },
    cornersDotOptions: {
      color: "#222222",
      type: "dot" as CornerDotType,
    },
  });

  const ref = useRef<HTMLDivElement>(null);
  const [qrCode] = useState<QRCodeStyling>(new QRCodeStyling(options));
  const [formError, setFormError] = useState<boolean>(false);
  const [formErrorMessage, setFormErrorMessage] = useState<string>("");

  // Variables for POST
  const [qrData, setQrData] = useState({
    userId: user!.uid,
    title: "",
    description: "",
    image: "",
    fullUrl: "",
    shortenedUrl: "",
    premium: true,
    group: "",
    expiry: "",
    password: "",
    totalVisits: 0,
  });

  const [modalShow, setModalShow] = useState<boolean>(false);

  const [patternState, changePatternState] = useState<Patterns>({
    activePattern: 0,
    patterns: [DotType_Square, DotType_Classy, DotType_Rounded],
  });

  const [cornerSquareState, changeCornerSquareState] = useState<Patterns>({
    activePattern: 0,
    patterns: [
      CornerSquare_Square,
      CornerSquare_Dot,
      CornerSquare_ExtraRounded,
    ],
  });

  const [cornerDotState, changeCornerDotState] = useState<Patterns>({
    activePattern: 0,
    patterns: [CornerDot_Dot, CornerDot_Square],
  });

  type Patterns = {
    activePattern: number;
    patterns: string[];
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (ref.current) {
      qrCode.append(ref.current);
    }
  }, [qrCode, ref]);

  useEffect(() => {
    if (!qrCode) return;
    qrCode.update(options);
  }, [qrCode, options]);

  useEffect(() => {
    checkQRLimit();
  }, []);

  const onDataChange = (event: ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(event.target.value);

    if (!isValidInput(event.target.value)) {
      setFormError(true);
      setFormErrorMessage(
        "Invalid input detected. Please remove special characters or scripts."
      );
      return;
    }

    setOptions((options) => ({
      ...options,
      data: sanitizedValue,
    }));

    setQrData({
      ...qrData,
      fullUrl: sanitizedValue,
    });
  };

  const onDownloadClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
    setFormError(false);
    setFormErrorMessage("");

    // Additional security validation
    const inputsToValidate = [
      qrData.title,
      qrData.fullUrl,
      qrData.description,
      qrData.group,
      qrData.password,
    ];

    if (inputsToValidate.some((input) => !isValidInput(input))) {
      setFormError(true);
      setFormErrorMessage(
        "Invalid input detected. Please remove any special characters or scripts."
      );
      return;
    }

    // Check if required fields are filled
    if (!qrData.fullUrl) {
      setFormError(true);
      setFormErrorMessage("Please enter a URL");
      return;
    }

    if (!qrData.title) {
      setFormError(true);
      setFormErrorMessage("Please enter a name/title");
      return;
    }

    // URL validation
    function isValidUrl(string: string) {
      try {
        new URL(string);
        return true;
      } catch (err) {
        return false;
      }
    }

    if (!isValidUrl(qrData.fullUrl)) {
      setFormError(true);
      setFormErrorMessage(
        "Please enter a valid URL (e.g., https://example.com)"
      );
      return;
    }

    // Name validation
    function isValidName(string: string) {
      if (string.length < 3) {
        return "Name must be at least 3 characters long";
      }
      if (string.length > 25) {
        return "Name must be less than 25 characters long";
      }
      return null;
    }

    const nameError = isValidName(qrData.title);
    if (nameError) {
      setFormError(true);
      setFormErrorMessage(nameError);
      return;
    }

    // If all validations pass
    setModalShow(true);
  };

  const onForegroundColourChange = (event: any) => {
    setOptions((options) => ({
      ...options,
      dotsOptions: {
        color: event.target.value,
      },
      cornersSquareOptions: {
        color: event.target.value,
      },
      cornersDotOptions: {
        color: event.target.value,
      },
    }));
  };

  const onBackgroundColourChange = (event: any) => {
    setOptions((options) => ({
      ...options,
      backgroundOptions: {
        color: event.target.value,
      },
    }));
  };

  const onDotTypeChange = (value: string) => {
    setOptions((options) => ({
      ...options,
      dotsOptions: {
        type: value as DotType,
      },
    }));
  };

  const onCornerSquareTypeChange = (value: string) => {
    setOptions((options) => ({
      ...options,
      cornersSquareOptions: {
        type: value as CornerSquareType,
      },
    }));
  };

  const onCornerDotTypeChange = (value: string) => {
    setOptions((options) => ({
      ...options,
      cornersDotOptions: {
        type: value as CornerDotType,
      },
    }));
  };

  function toggleActivePattern(index: number) {
    changePatternState({
      ...patternState,
      activePattern: index,
    });
  }

  function toggleActiveCornerSquare(index: number) {
    changeCornerSquareState({
      ...cornerSquareState,
      activePattern: index,
    });
  }

  function toggleActiveCornerDot(index: number) {
    changeCornerDotState({
      ...cornerDotState,
      activePattern: index,
    });
  }

  function handleFile(event: any) {
    const formData = new FormData();
    formData.append("file", event.target.files[0]);

    setOptions((options) => ({
      ...options,
      image: URL.createObjectURL(event.target.files[0]),
    }));
  }

  function removeFile() {
    setOptions((options) => ({
      ...options,
      image: "",
    }));
  }

  async function checkQRLimit() {
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/qrcodes/count/${user!.uid}`,
        {
          headers: {
            authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `HTTP Error: ${response.status} - ${response.statusText}`
        );
      }

      const responseData = await response.json();
      const qrCount = responseData.data;

      if (!premium && !business) {
        if (qrCount >= 5) {
          navigate("/dashboard/plans/qrlimit");
        }
      } else if (premium && !business) {
        if (qrCount >= 10) {
          navigate("/dashboard/plans/qrlimit");
        }
      } else if (business) {
        if (qrCount >= 100) {
          navigate("/dashboard/plans/qrlimit");
        }
      }
    } catch (error) {
      console.error("Error checking QR limit:", error);
    }
  }

  const handleInputChange = (field: keyof typeof qrData, value: string) => {
    const sanitizedValue = sanitizeInput(value);

    // Check for malicious input
    if (!isValidInput(value)) {
      setFormError(true);
      setFormErrorMessage(
        "Invalid input detected. Please remove special characters or scripts."
      );
      return;
    }

    // Check length limits
    const maxLengths: Record<string, number> = {
      title: MAX_TITLE_LENGTH,
      description: MAX_DESCRIPTION_LENGTH,
      password: MAX_PASSWORD_LENGTH,
    };

    if (maxLengths[field] && value.length > maxLengths[field]) {
      setFormError(true);
      setFormErrorMessage(
        `${field} must be less than ${maxLengths[field]} characters`
      );
      return;
    }

    setQrData({
      ...qrData,
      [field]: sanitizedValue,
    });
  };

  return (
    <div className="qrCodeCreation">
      <QRCodeModal
        show={modalShow}
        options={options}
        qrdata={qrData}
        onHide={() => setModalShow(false)}
      />

      <h1 className="overview__container__header">QR Creation</h1>
      <Breadcrumb className="overview__container__breadcrumbs">
        <Breadcrumb.Item href="/home">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item href="/dashboard/qrcodes">Qr Codes</Breadcrumb.Item>
        <Breadcrumb.Item active>Create New QRCode</Breadcrumb.Item>
      </Breadcrumb>
      <div className="qrCodeCreation__container">
        <form className="qrCodeCreation__container__customization">
          <div ref={ref} className="qrPreview" />
          <Accordion>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Colours</Accordion.Header>
              <Accordion.Body>
                <div className="qrCodeCreation__container__form__field">
                  <label htmlFor="foreground-color">Foreground Colour</label>
                  <div className="qrCodeCreation__container__form__field__row">
                    <Form.Control
                      type="color"
                      id="foreground-color"
                      value={options.dotsOptions?.color}
                      onChange={(e) => {
                        onForegroundColourChange(e);
                      }}
                      aria-label="Choose foreground color for QR code"
                    />
                    <input
                      className="qrCodeCreation__container__customization__input"
                      type="text"
                      placeholder="#5E32A8"
                      value={options.dotsOptions?.color}
                      onChange={(e) => {
                        onForegroundColourChange(e);
                      }}
                      aria-label="Enter foreground color hex code"
                    />
                  </div>
                </div>
                <div className="qrCodeCreation__container__form__field">
                  <label htmlFor="background-color">Background Colour</label>
                  <div className="qrCodeCreation__container__form__field__row">
                    <Form.Control
                      type="color"
                      id="background-color"
                      value={options.backgroundOptions?.color}
                      onChange={(e) => {
                        onBackgroundColourChange(e);
                      }}
                      aria-label="Choose background color for QR code"
                    />
                    <input
                      className="qrCodeCreation__container__customization__input"
                      type="text"
                      placeholder="#FFFF"
                      value={options.backgroundOptions?.color}
                      onChange={(e) => {
                        onBackgroundColourChange(e);
                      }}
                      aria-label="Enter background color hex code"
                    />
                  </div>
                </div>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>Dot Type</Accordion.Header>
              <Accordion.Body>
                Here you can select a different QR Pattern.
                <div
                  className="qrCodeCreation__container__customization__options"
                  role="radiogroup"
                  aria-label="QR code dot pattern options"
                >
                  {patternState.patterns.map((elements, index) => (
                    <div
                      key={index}
                      className={`qrCodeCreation__container__customization__options__item ${
                        patternState.activePattern === index
                          ? "active_option"
                          : ""
                      }`}
                      onClick={() => {
                        if (index === 0) {
                          onDotTypeChange("square");
                        } else if (index === 1) {
                          onDotTypeChange("classy");
                        } else if (index === 2) {
                          onDotTypeChange("rounded");
                        }

                        toggleActivePattern(index);
                      }}
                      role="radio"
                      aria-checked={patternState.activePattern === index}
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          if (index === 0) {
                            onDotTypeChange("square");
                          } else if (index === 1) {
                            onDotTypeChange("classy");
                          } else if (index === 2) {
                            onDotTypeChange("rounded");
                          }
                          toggleActivePattern(index);
                        }
                      }}
                    >
                      <img
                        src={elements}
                        alt={`QR code pattern option ${index + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>Corner Square</Accordion.Header>
              <Accordion.Body>
                Here you can select a different square type for your QR Code.
                <div
                  className="qrCodeCreation__container__customization__options"
                  role="radiogroup"
                  aria-label="QR code corner square options"
                >
                  {cornerSquareState.patterns.map((elements, index) => (
                    <div
                      key={index}
                      className={`qrCodeCreation__container__customization__options__item ${
                        cornerSquareState.activePattern === index
                          ? "active_option"
                          : ""
                      }`}
                      onClick={() => {
                        if (index === 0) {
                          onCornerSquareTypeChange("square");
                        } else if (index === 1) {
                          onCornerSquareTypeChange("dot");
                        } else if (index === 2) {
                          onCornerSquareTypeChange("extra-rounded");
                        }

                        toggleActiveCornerSquare(index);
                      }}
                      role="radio"
                      aria-checked={cornerSquareState.activePattern === index}
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          if (index === 0) {
                            onCornerSquareTypeChange("square");
                          } else if (index === 1) {
                            onCornerSquareTypeChange("dot");
                          } else if (index === 2) {
                            onCornerSquareTypeChange("extra-rounded");
                          }
                          toggleActiveCornerSquare(index);
                        }
                      }}
                    >
                      <img
                        src={elements}
                        alt={`QR code corner square option ${index + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="3">
              <Accordion.Header>Corner Dot</Accordion.Header>
              <Accordion.Body>
                Here you can select a different dot type for your QR Code.
                <div
                  className="qrCodeCreation__container__customization__options"
                  role="radiogroup"
                  aria-label="QR code corner dot options"
                >
                  {cornerDotState.patterns.map((elements, index) => (
                    <div
                      key={index}
                      className={`qrCodeCreation__container__customization__options__item ${
                        cornerDotState.activePattern === index
                          ? "active_option"
                          : ""
                      }`}
                      onClick={() => {
                        if (index === 0) {
                          onCornerDotTypeChange("dot");
                        } else if (index === 1) {
                          onCornerDotTypeChange("square");
                        }

                        toggleActiveCornerDot(index);
                      }}
                      role="radio"
                      aria-checked={cornerDotState.activePattern === index}
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          if (index === 0) {
                            onCornerDotTypeChange("dot");
                          } else if (index === 1) {
                            onCornerDotTypeChange("square");
                          }
                          toggleActiveCornerDot(index);
                        }
                      }}
                    >
                      <img
                        src={elements}
                        alt={`QR code corner dot option ${index + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="4">
              <Accordion.Header>Image</Accordion.Header>
              <Accordion.Body>
                Here you can select a Image or Logo to add to your QR Code.
                <div className="qrCodeCreation__container__customization__options">
                  <div className="image-field-container">
                    <Form.Group controlId="formFile" className="mb-3">
                      <Form.Control
                        type="file"
                        onChange={(e) => {
                          handleFile(e);
                        }}
                        disabled={!premium && !business}
                        aria-label="Upload image or logo for QR code"
                      />
                    </Form.Group>
                    {!premium && !business && (
                      <div className="premium-feature-message">
                        <MdLockReset className="lock-icon" />
                        <span>Premium Plan Feature</span>
                      </div>
                    )}
                    {options.image && (
                      <AiFillDelete
                        className="qrCodeCreation__container__customization__options__reset-btn"
                        onClick={() => removeFile()}
                        aria-label="Remove uploaded image"
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            removeFile();
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </form>

        <form action="" className="qrCodeCreation__container__form">
          <div className="qrCodeCreation__container__form__field">
            <label htmlFor="qr-name">
              Name <span>*</span>
            </label>
            <input
              id="qr-name"
              type="text"
              placeholder="Enter a name/title"
              onChange={(e) => handleInputChange("title", e.target.value)}
              aria-required="true"
            />
          </div>

          <div className="qrCodeCreation__container__form__field">
            <label htmlFor="qr-link">
              Link <span>*</span>
            </label>
            <input
              id="qr-link"
              type="text"
              placeholder="Enter URL Link"
              onChange={(e) => handleInputChange("fullUrl", e.target.value)}
              aria-required="true"
            />
          </div>

          <div className="qrCodeCreation__container__form__field">
            <label htmlFor="qr-description">Description</label>
            <input
              id="qr-description"
              type="text"
              placeholder="Enter a short description"
              onChange={(e) => handleInputChange("description", e.target.value)}
            />
          </div>

          <div className="qrCodeCreation__container__form__field">
            <label htmlFor="qr-expiry">Expiry (Leave blank if Never)</label>
            <select
              id="qr-expiry"
              onChange={(e) => {
                setQrData({
                  ...qrData,
                  expiry: e.target.value,
                });
              }}
              value={qrData.expiry}
            >
              <option value="">Never</option>
              <option value="30">30 Days</option>
              <option value="60">60 Days</option>
              <option value="90">90 Days</option>
              <option value="180">180 Days</option>
              <option value="365">365 Days</option>
            </select>
          </div>

          <div className="qrCodeCreation__container__form__field">
            <label htmlFor="qr-group">Group</label>
            <div className="password-field-container">
              <input
                id="qr-group"
                type="text"
                placeholder="Choose a group"
                onChange={(e) => handleInputChange("group", e.target.value)}
                disabled={!premium && !business}
              />
              {!premium && !business && (
                <div className="premium-feature-message">
                  <MdLockReset className="lock-icon" />
                  <span>Premium Plan Feature</span>
                </div>
              )}
            </div>
          </div>

          <div className="qrCodeCreation__container__form__field">
            <label htmlFor="">Password</label>
            <div className="password-field-container">
              <input
                type="text"
                placeholder="Enter a password"
                onChange={(e) => handleInputChange("password", e.target.value)}
                disabled={!premium && !business}
              />
              {!premium && !business && (
                <div className="premium-feature-message">
                  <MdLockReset className="lock-icon" />
                  <span>Premium Plan Feature</span>
                </div>
              )}
            </div>
          </div>

          {formError && <b className="form__error">{formErrorMessage}</b>}

          <button
            className="qrCodeCreation__container__form__submit"
            onClick={(e) => onDownloadClick(e)}
            aria-label="Create QR code"
          >
            Create QR
          </button>
        </form>
      </div>
    </div>
  );
};

export default QRCodeCreation;
